import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { formatUkDateTime } from '../lib/date';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const QuoteAcceptance = () => {
  const { bookingId } = useParams();
  const [searchParams] = useSearchParams();
  const [booking, setBooking] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const quoteAmount = searchParams.get('amount');

  const fetchBooking = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/bookings/${bookingId}`);
      setBooking(response.data);
    } catch (error) {
      console.error('Error fetching booking:', error);
      toast.error('Booking not found');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);


  const handleAcceptance = async (accept) => {
    try {
      setSubmitting(true);
      const response = await axios.post(`${API}/bookings/${bookingId}/accept-quote`, {
        accept: accept
      });

      setResult({
        success: accept,
        message: response.data.message,
        driverLink: response.data.driverLink
      });

      if (accept) {
        toast.success('Quote accepted! Booking confirmed.');
      } else {
        toast.info('Quote declined. Thank you for considering us.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process your response');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-black mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quote details...</p>
        </div>
      </div>
    );
  }

  if (!booking) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center">
        <Card className="max-w-md mx-auto p-8 text-center">
          <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Booking Not Found</h1>
          <p className="text-gray-600">The booking reference you're looking for doesn't exist.</p>
        </Card>
      </div>
    );
  }

  if (result) {
    return (
      <div className="min-h-screen bg-white pt-20 flex items-center justify-center px-6">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          {result.success ? (
            <>
              <CheckCircle2 className="h-20 w-20 text-green-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold mb-4 text-black">Booking Confirmed!</h1>
              <p className="text-lg text-gray-600 mb-6">{result.message}</p>
              <div className="bg-gray-50 p-6 rounded-lg mb-6">
                <h3 className="font-semibold text-black mb-4">What Happens Next?</h3>
                <div className="text-left space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">1</div>
                    <p className="text-gray-700">You'll receive a confirmation email shortly</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">2</div>
                    <p className="text-gray-700">We'll contact you 24 hours before your journey</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-black text-white flex items-center justify-center flex-shrink-0 text-xs font-bold">3</div>
                    <p className="text-gray-700">Your chauffeur will arrive at the scheduled time</p>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <XCircle className="h-20 w-20 text-gray-500 mx-auto mb-6" />
              <h1 className="text-3xl font-bold mb-4 text-black">Quote Declined</h1>
              <p className="text-lg text-gray-600 mb-6">Thank you for considering Obsidian Chauffeurs.</p>
              <p className="text-gray-600">If you'd like to discuss alternative options, please don't hesitate to contact us.</p>
            </>
          )}
          <Button
            onClick={() => window.location.href = '/'}
            className="bg-black text-white hover:bg-gray-800 mt-6"
          >
            Return to Home
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white pt-32 px-6 pb-12">
      <div className="max-w-3xl mx-auto">
        <Card className="p-8">
          <div className="text-center mb-8">
            <Clock className="h-16 w-16 text-black mx-auto mb-4" />
            <h1 className="text-3xl font-bold mb-2 text-black">Quote for Your Journey</h1>
            <p className="text-gray-600">Booking Reference: <span className="font-mono font-bold text-black">{booking.id}</span></p>
          </div>

          {/* Journey Details */}
          <div className="bg-gray-50 p-6 rounded-lg mb-8">
            <h3 className="font-bold text-xl mb-4 text-black">Journey Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-600">Service</p>
                <p className="font-semibold text-black">{booking.serviceType}</p>
              </div>
              <div>
                <p className="text-gray-600">Date & Time</p>
                <p className="font-semibold text-black">{formatUkDateTime(booking.pickupDate, booking.pickupTime)}</p>
              </div>
              <div>
                <p className="text-gray-600">Pickup Location</p>
                <p className="font-semibold text-black">{booking.pickupLocation}</p>
              </div>
              <div>
                <p className="text-gray-600">Drop-off Location</p>
                <p className="font-semibold text-black">{booking.dropoffLocation}</p>
              </div>
              <div>
                <p className="text-gray-600">Passengers</p>
                <p className="font-semibold text-black">{booking.passengers} passengers</p>
              </div>
              <div>
                <p className="text-gray-600">Luggage</p>
                <p className="font-semibold text-black">{booking.luggage} pieces</p>
              </div>
              {booking.returnRequired === 'yes' && (
                <div className="md:col-span-2 mt-2 border-t-2 border-black pt-4" data-testid="quote-return-journey">
                  <p className="text-xs font-bold uppercase tracking-wider text-black mb-2">Return Journey</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-gray-600">Return Date &amp; Time</p>
                      <p className="font-semibold text-black">{formatUkDateTime(booking.returnDate, booking.returnTime)}</p>
                    </div>
                    <div>
                      <p className="text-gray-600">Return Route</p>
                      <p className="font-semibold text-black">{booking.dropoffLocation} &rarr; {booking.pickupLocation}</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Quote Amount */}
          {quoteAmount && (
            <div className="bg-black text-white p-8 rounded-lg mb-8 text-center">
              <p className="text-white/70 mb-2">Total Quote</p>
              <p className="text-5xl font-bold">£{parseFloat(quoteAmount).toFixed(2)}</p>
              <p className="text-white/70 text-sm mt-2">All inclusive - no hidden charges</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={() => handleAcceptance(false)}
              variant="outline"
              className="flex-1 border-2 border-gray-300 text-gray-700 hover:bg-gray-100"
              disabled={submitting || booking.status !== 'quote_sent'}
            >
              Decline Quote
            </Button>
            <Button
              onClick={() => handleAcceptance(true)}
              className="flex-1 bg-black text-white hover:bg-gray-800"
              disabled={submitting || booking.status !== 'quote_sent'}
            >
              {submitting ? 'Processing...' : 'Accept & Confirm Booking'}
            </Button>
          </div>

          {booking.status !== 'quote_sent' && (
            <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800 text-center">
                This quote has already been {booking.quoteAccepted ? 'accepted' : 'processed'}.
              </p>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};

export default QuoteAcceptance;
